import { create } from 'zustand';

export type NotificationKind =
  | 'order_sent_to_cashier'
  | 'order_returned_to_doctor'
  | 'order_charged'
  | 'order_cancelled'
  | 'cash_difference'
  | 'otp_used'
  | 'low_stock';

export type NotificationAudience = 'cashier' | 'doctor' | 'admin' | 'inventory';

export interface Notification {
  id: string;
  kind: NotificationKind;
  branch_id: string | null;
  audience: NotificationAudience[];
  title_key: string;
  body_key: string;
  body_args: Record<string, string | number | null>;
  reference_kind: string | null;
  reference_id: string | null;
  amount_cents: string | null;
  created_at: string;
  read: boolean;
}

interface NotificationsState {
  items: Notification[];
  latest_seen_id: string | null;
  /** Ingest a batch of newly-fetched notifications. Returns the ones that weren't seen before. */
  ingest: (incoming: Omit<Notification, 'read'>[]) => Notification[];
  markAllRead: () => void;
  clear: () => void;
  setLatestSeenId: (id: string | null) => void;
}

const FEED_CAP = 200;

export const useNotificationsStore = create<NotificationsState>((set, get) => ({
  items: [],
  latest_seen_id: null,
  ingest: (incoming) => {
    const existing_ids = new Set(get().items.map((it) => it.id));
    const fresh = incoming.filter((n) => !existing_ids.has(n.id));
    if (fresh.length === 0) return [];
    const new_items = fresh.map((n) => ({ ...n, read: false }));
    set((state) => ({
      items: [...new_items, ...state.items].slice(0, FEED_CAP),
    }));
    return new_items;
  },
  markAllRead: () =>
    set((state) => ({
      items: state.items.map((it) => ({ ...it, read: true })),
    })),
  clear: () => set({ items: [], latest_seen_id: null }),
  setLatestSeenId: (id) => set({ latest_seen_id: id }),
}));
